import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";
import {
  GatingConfigurationResponse,
  GatingTriggerType,
} from "src/types/experiences/storyboardSettings";

export const INTERACTIVE_CONTENT_TYPES: StoryboardType[] = [
  "INTERACTIVE_CONTENT",
  "INTERACTIVE_CONTENT_V2",
];

export const SLIDE_STORYBOARDS: StoryboardType[] = [
  "SPATIAL",
  "SLIDE_STORYBOARD",
  "FLIP_BOOK",
];

export const isInteractiveContent = (type?: StoryboardType): boolean =>
  !!type && INTERACTIVE_CONTENT_TYPES.includes(type);

export const isSlideStoryboard = (type?: StoryboardType): boolean =>
  !!type && SLIDE_STORYBOARDS.includes(type);

export const validateName = (name?: string): string | null => {
  if (!name || !name.trim()) return "Please provide a valid name";
  return null;
};

const INVALID_ALIAS_CHARS = /^[a-z_-]|[`\s!@#$%^&*()+\=\[\]{};':"\\|,.<>\?~]/;
const DOUBLE_SLASH_INSIDE = /([^:]\/)\/+/;

export const validateAlias = (
  alias?: string,
  type?: StoryboardType,
): string | null => {
  if (!isInteractiveContent(type)) return null;
  if (!alias || !alias.trim()) return "Please provide valid alias";
  if (alias.startsWith("//")) return "Please provide valid alias";
  if (INVALID_ALIAS_CHARS.test(alias)) return "Please provide valid alias";
  if (DOUBLE_SLASH_INSIDE.test(alias)) return "Please provide valid alias";
  return null;
};

// Trigger types that fire on an event rather than a measurable value, so they
// don't require a numeric value (mirrors TRIGGERS_WITHOUT_VALUE in the gating
// card constants — kept in sync but inlined to avoid a component import here).
const TRIGGERS_WITHOUT_VALUE: GatingTriggerType[] = [
  "INTERACTIVE_DOWNLOAD",
  "EXIT_INTENT",
  "SMART",
  "AUTO_GATING",
];

export const validateGatings = (
  gatings?: GatingConfigurationResponse[],
): string | null => {
  if (!gatings?.length) return null;

  for (const gating of gatings) {
    if (!gating?.enabled) continue;

    const label = gating.autoGating
      ? "Auto Gating"
      : gating.name?.trim() || "gating";

    const hasScreen = Boolean(gating.gating?.id);
    if (!hasScreen) {
      return `Please select a gating screen for ${label}`;
    }

    if (gating.autoGating) {
      if (!gating.conversionType) {
        return `A gating screen is required. Please select one.`;
      }
      continue;
    }

    if (gating.smart) continue;

    const trigger = gating.trigger;
    if (!trigger?.type) {
      return `Please configure a trigger for ${label}`;
    }
    const needsValue = !TRIGGERS_WITHOUT_VALUE.includes(trigger.type);
    if (needsValue && (trigger.value == null || trigger.value === "")) {
      return `Please set a trigger value for ${label}`;
    }
  }

  return null;
};

interface ServerErrorLike {
  errors?: Array<{ message?: string }>;
  message?: string;
}

export const mapServerErrorToToast = (err: unknown): string => {
  const e = err as ServerErrorLike;
  const code = e?.errors?.[0]?.message;
  if (code === "alias.duplicate") return "The entered Site URL already exists";
  return "Unable to update storyboard";
};
